import {
  DocumentData,
  FirestoreError,
  Timestamp,
  UpdateData,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getFirestore,
  onSnapshot,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { Utilities } from "../../../utils/utilities";
import { CreatedByData } from "./CreatedByData";
import { LocalData } from "./LocalData";
import { PropertyData } from "../Property/PropertyData";
import { Debug } from "@src/utils/debug";

export class FetchStatistics {
  static fetchCount = 0;
}

export class DocumentLoadedListener {
  id = Utilities.generateUUID();
  data: FirebaseData;
  callback: (error?: FirestoreError) => void;
  constructor(callback: (error?: FirestoreError) => void, data: FirebaseData) {
    this.callback = callback;
    this.data = data;
  }
  removeListener() {
    this.data.removeListener(this);
  }
}

export class FirebaseRealtimeLister {
  callback: () => void;
  constructor(callback: () => void) {
    this.callback = callback;
  }
  removeListener() {
    this.callback();
  }
}

/**
 * @deprecated Use React-Query based data fetching instead.
 * More information on Notion or ask the team on Slack.
 */
export class FirebaseData {
  id: string = "";
  deleted: boolean = false;
  loaded: boolean = false;
  loading: boolean = false;
  loadedListeners: DocumentLoadedListener[] = [];
  realtimeListeners: DocumentLoadedListener[] = [];
  createdBy?: CreatedByData;
  updatedBy?: CreatedByData;
  parent?: FirebaseData & { properties?: PropertyData[] };
  collectionName: string;
  rawData: DocumentData = {};
  fetchCount = 0;
  realtimeListener?: () => void;

  constructor(id: string, collectionName: string, parent: FirebaseData | undefined) {
    this.id = id;
    this.collectionName = collectionName;
    this.parent = parent;
  }

  setup(data: any) {
    if (this.isValidObject(data.createdBy)) {
      this.createdBy = new CreatedByData(data.createdBy);
    }
    if (this.isValidObject(data.updatedBy)) {
      this.updatedBy = new CreatedByData(data.updatedBy);
    }
    if (this.isValidObject(data.deletedBy)) {
      this.deleted = true;
    }
    this.rawData = data;
    this.loaded = true;
  }

  load(): Promise<this> {
    return new Promise<this>((resolve, reject) => {
      const path = this.getCompletePath();

      const listener = new DocumentLoadedListener((error) => {
        if (error) reject(error);
        else resolve(this);
      }, this);
      this.loadedListeners.push(listener);
      if (!this.loading && this.id) {
        this.loading = true;

        this.fetchCount++;
        FetchStatistics.fetchCount++;
        Debug.print(`[${path}] Fetching ${this.fetchCount} / ${FetchStatistics.fetchCount}`);
        getDoc(doc(getFirestore(), path))
          .then((snapshot) => {
            this.loaded = true;
            this.loading = false;
            if (!snapshot.exists()) {
              this.deleted = true;
            } else {
              this.setup(snapshot.data() ?? {});
            }

            this.loadedListeners.forEach((listener) => {
              listener.callback(undefined);
            });
            this.loadedListeners = [];
          })
          .catch((error) => {
            this.loaded = true;
            this.loading = false;
            this.deleted = true;

            this.loadedListeners.forEach((listener) => {
              listener.callback(error);
            });
            this.loadedListeners = [];
          });
      }
    });
  }

  setupRealtimeListener(onUpdate: () => void): DocumentLoadedListener {
    const path = this.getCompletePath();
    const listener = new DocumentLoadedListener(onUpdate, this);
    this.realtimeListeners.push(listener);

    if (!this.realtimeListener) {
      this.fetchCount++;
      FetchStatistics.fetchCount++;
      Debug.print(`[${path}] Fetching ${this.fetchCount} / ${FetchStatistics.fetchCount}`);
      this.realtimeListener = onSnapshot(
        doc(getFirestore(), path),
        (doc) => {
          const data = doc.data();
          if (data) {
            this.setup(data);
          }
          if (!doc.exists()) this.deleted = true;

          this.loaded = true;
          this.realtimeListeners.forEach((listener) => {
            listener.callback();
          });
        },
        (error) => {
          console.error("Error setting up realtime listener");
          console.error(path);
          console.error(error.message);
          this.loaded = true;
          this.deleted = true;
          this.realtimeListeners.forEach((listener) => {
            listener.callback(error);
          });
        }
      );
    }

    return listener;
  }

  /** @deprecated Should be replaced by .load */
  loadWithCompletionHandler(completion: (error?: FirestoreError) => void): DocumentLoadedListener {
    const path = this.getCompletePath();

    const listener = new DocumentLoadedListener(completion, this);
    this.loadedListeners.push(listener);
    if (!this.loading && this.id) {
      this.loading = true;
      this.fetchCount++;
      FetchStatistics.fetchCount++;
      Debug.print(`[${path}] Fetching ${this.fetchCount} / ${FetchStatistics.fetchCount}`);
      getDoc(doc(getFirestore(), path))
        .then((snapshot) => {
          this.loaded = true;
          this.loading = false;
          if (!snapshot.exists()) {
            this.deleted = true;
          } else {
            this.setup(snapshot.data());
          }

          this.loadedListeners.forEach((listener) => {
            listener.callback(undefined);
          });
          this.loadedListeners = [];
        })
        .catch((error: FirestoreError) => {
          this.loaded = true;
          this.loading = false;
          this.deleted = true;

          this.loadedListeners.forEach((listener) => {
            listener.callback(error);
          });
          this.loadedListeners = [];
        });
    }
    return listener;
  }

  deleteDocument() {
    this.deleted = true;
    return deleteDoc(doc(getFirestore(), this.getCompletePath()))
      .then(() => {
        this.deleted = true;
        return undefined;
      })
      .catch((error) => {
        FirebaseData.saveErrorLog("delete", this.getCompletePath(), {}, error);
        this.deleted = false;

        throw error;
      });
  }

  /** @deprecated Should be replaced by .deleteDocument */
  deleteDocumentWithCompletionHandler(completion: (error?: FirestoreError) => void) {
    deleteDoc(doc(getFirestore(), this.getCompletePath()))
      .then(() => {
        this.deleted = true;
        completion(undefined);
      })
      .catch((error: FirestoreError) => {
        FirebaseData.saveErrorLog("delete", this.getCompletePath(), {}, error);
        completion(error);
      });
  }

  removeAllRealtimeListeners() {
    this.realtimeListener?.();
    this.realtimeListener = undefined;
    this.realtimeListeners = [];
  }
  removeListener(listener?: DocumentLoadedListener) {
    this.loadedListeners = this.loadedListeners.filter((list) => list.id !== listener?.id);
    this.realtimeListeners = this.realtimeListeners.filter((list) => list.id !== listener?.id);

    if (this.realtimeListeners.length === 0) {
      this.removeAllRealtimeListeners();
    }
  }

  /** @deprecated Should be replaced by .saveUpdate */
  saveUpdateWithCompletionHandler(data: DocumentData, completion?: (error?: FirestoreError) => void) {
    const path = this.getCompletePath();
    const saveData = {
      updatedBy: {
        userID: LocalData.currentUser.id,
        date: Timestamp.fromDate(new Date()),
      },
      ...data,
    };
    updateDoc(doc(getFirestore(), path), saveData)
      .then(() => {
        if (completion) completion(undefined);
      })
      .catch((error: FirestoreError) => {
        console.log("Failed to upload data to path " + path + ". " + error.code + " : " + error.message);
        FirebaseData.saveErrorLog("update", path, saveData, error);
        if (completion) completion(error);
      });
  }

  saveUpdate(data: DocumentData): Promise<this> {
    const path = this.getCompletePath();
    const saveData = {
      updatedBy: {
        userID: LocalData.currentUser.id,
        date: Timestamp.fromDate(new Date()),
      },
      ...data,
    };

    return updateDoc(doc(getFirestore(), path), saveData)
      .then(() => this)
      .catch((error: FirestoreError) => {
        console.log("Failed to upload data to path " + path + ". " + error.code + " : " + error.message);
        FirebaseData.saveErrorLog("update", path, saveData, error);

        throw error;
      });
  }

  saveWrite(data: DocumentData): Promise<this> {
    const path = this.getCompletePath();
    const saveData = {
      updatedBy: {
        userID: LocalData.currentUser.id,
        date: Timestamp.fromDate(new Date()),
      },
      ...data,
    };

    return setDoc(doc(getFirestore(), path), saveData, { merge: true })
      .then(() => this)
      .catch((error: FirestoreError) => {
        console.log("Failed to upload data to path " + path + ". " + error.code + " : " + error.message);
        FirebaseData.saveErrorLog("write", path, saveData, error);
        throw error;
      });
  }

  /** @deprecated should be replaced by .saveWrite */
  saveWriteWithCompletionHandler(data: DocumentData, completion: (error?: FirestoreError) => void) {
    const path = this.getCompletePath();
    const saveData = {
      updatedBy: {
        userID: LocalData.currentUser.id,
        date: Timestamp.fromDate(new Date()),
      },
      ...data,
    };
    setDoc(doc(getFirestore(), path), saveData, { merge: true })
      .then(() => {
        completion(undefined);
      })
      .catch((error: FirestoreError) => {
        console.log("Failed to upload data to path " + path + ". " + error.code + " : " + error.message);
        FirebaseData.saveErrorLog("write", path, saveData, error);
        completion(error);
      });
  }

  public static saveErrorLog(method: string, path: string, data: { [key: string]: any }, error: FirestoreError) {
    addDoc(collection(getFirestore(), "errorLogs"), {
      date: new Date(),
      userID: LocalData.currentUser.id ?? "",
      method,
      path,
      data,
      error: { code: error?.code ?? "", message: error?.message ?? "", stack: error?.stack ?? "", name: error?.name ?? "" },
    })
      .then(() => console.log("Saved error log"))
      .catch((error) => console.log("Failed to save error log", error));
  }

  getCompletePath(): string {
    let path = this.collectionName + "/" + this.id;
    let parent = this.parent;
    while (parent) {
      path = parent.collectionName + "/" + parent.id + "/" + path;
      parent = parent.parent;
    }
    return path;
  }

  /** @deprecated */
  protected isValidString(value: any) {
    return (typeof value === "string" || value instanceof String) && value !== "";
  }

  /** @deprecated */
  protected isValidNumber(value: any) {
    return typeof Utilities.convertToNumber(value) === "number" && isFinite(Utilities.convertToNumber(value));
  }

  /** @deprecated */
  protected isValidObject(value: any) {
    return value && typeof value === "object" && value.constructor === Object;
  }

  /** @deprecated */
  protected isValidBoolean(value: any) {
    return typeof value === "boolean";
  }

  /** @deprecated */
  protected isValidDate(value: any) {
    return value instanceof Date;
  }

  /** @deprecated */
  protected toDate(object: any) {
    if (object) {
      if (object.seconds) {
        return new Date(object.seconds * 1000);
      }
    }
    return undefined;
  }

  /**
   * @deprecated use globally imported generateDocumentID instead
   */
  static generateID(): string {
    return doc(collection(getFirestore(), "PropelyAS")).id;
  }
}
